import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { t } from '../i18n.js';
import { formatDateTime, formatSize } from '../format.js';
import { editor, editorDirty, me, patchEditor, type ConflictState } from '../state.js';
import {
  onCloseEditor,
  onLoadHistory,
  onReloadNote,
  onRename,
  onRestore,
  onSaveNote,
  onStayInEditor,
} from '../actions.js';
import { dismissOverlay } from '../navigation.js';
import { Action, IconButton, Pill } from './parts.js';
import { Popover } from './popover.js';
import {
  IconCheck,
  IconClose,
  IconCloudUp,
  IconCode,
  IconEdit,
  IconHistory,
  IconMore,
} from './icons.js';
import { useDialog } from './dialog.js';
import { focusRichNote, FormatBar, RichNote } from './rich-note.js';

/**
 * **L'éditeur de notes** — le cœur de l'étape 4, et la seule surface du client
 * où deux appareils peuvent se contredire.
 *
 * Trois règles le dessinent, et aucune n'est un choix esthétique :
 *
 * ① **On enregistre sur événement, jamais à la frappe** (D20). Un enregistrement
 *    = un item `REV#` : sauver en continu produirait une révision par caractère.
 *    Les événements sont la fermeture, le bouton, `Ctrl+S`, et le passage de
 *    l'onglet en arrière-plan — celui-là est le seul qui rattrape un onglet fermé.
 * ② **Un conflit se montre, il ne se résout pas tout seul.** Fusionner du
 *    Markdown en silence produit des blocs dupliqués et des suppressions
 *    annulées ; c'est la perte de données que le verrou existe pour rendre
 *    visible.
 * ③ **L'historique est réservé au propriétaire** (D22), et son bouton est donc
 *    *absent* sur une note reçue, pas grisé : un contrôle désactivé annonce une
 *    fonction qui n'existe pas pour cet utilisateur.
 */
export function Editor(): JSX.Element | null {
  const state = editor.value;
  const area = useRef<HTMLTextAreaElement>(null);
  // **Le focus reste dans l'éditeur**, mais `Échap` n'est pas délégué : la règle
  // locale plus bas retient la fermeture tant qu'un conflit n'est pas tranché, et
  // une seconde sortie l'aurait contournée.
  const host = useDialog(state !== undefined, { lockScroll: true });

  // Le rattrapage de l'onglet quitté. `visibilitychange` est le dernier moment
  // fiable pour écrire : `beforeunload` n'autorise plus de requête asynchrone, et
  // `unload` ne s'exécute pas du tout sur mobile.
  useEffect(() => {
    if (!state) return;
    const onHide = () => {
      if (document.visibilityState === 'hidden') void onSaveNote();
    };
    document.addEventListener('visibilitychange', onHide);
    return () => document.removeEventListener('visibilitychange', onHide);
  }, [state !== undefined]);

  useEffect(() => {
    if (state?.mode === 'source') area.current?.focus();
    else focusRichNote();
  }, [state?.mode]);

  if (!state) return null;

  const owner = state.entity.Owner === undefined || state.entity.Owner === me.value?.uid;
  // La sortie passe par l'historique quand c'est l'éditeur qui y est inscrit : le
  // geste système et la croix doivent enregistrer de la même façon.
  const close = (): void => dismissOverlay('editor', () => void onCloseEditor());

  const onKeyDown = (event: JSX.TargetedKeyboardEvent<HTMLDivElement>) => {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
      // Le navigateur enregistrerait la page ; c'est le seul raccourci qu'il faut
      // vraiment lui reprendre, et l'attente est universelle dans un éditeur.
      event.preventDefault();
      void onSaveNote();
    }
    if (event.key === 'Escape' && !state.conflict) close();
  };

  return (
    <div
      class="editor"
      ref={host}
      tabIndex={-1}
      data-testid="editor"
      role="dialog"
      aria-modal="true"
      aria-label={state.entity.Name}
      onKeyDown={onKeyDown}
    >
      {/* **La barre tient sur un téléphone, et elle porte le titre**
          *(2026-08-14)*.

          Elle alignait quatre contrôles en fin de rangée — mode, historique,
          enregistrer, fermer — dont deux portaient un libellé. À 390 px, la
          rangée débordait par la droite et « Enregistrer » se retrouvait à moitié
          hors de l'écran : le geste central de l'éditeur était le premier à
          disparaître.

          Trois choses changent, et la première est la seule qui compte : **il ne
          reste que ce qu'on fait à chaque note** — fermer, nommer, enregistrer.
          Le mode d'affichage et l'historique sont des gestes rares, ils passent
          derrière un « … » (point 16). Le titre, lui, devient un champ (point
          15) : il fallait fermer l'éditeur et ouvrir la fiche pour renommer ce
          qu'on venait d'écrire. */}
      <div class="reader-bar editor-bar">
        <IconButton testId="editor-close" label={t('viewer.close')} onClick={close}>
          <IconClose />
        </IconButton>

        <TitleField />

        <Pill testId="editor-rev" tone={editorDirty.value ? 'warning' : 'neutral'}>
          {editorDirty.value
            ? t('editor.unsaved')
            : t('editor.revision', { rev: state.baseRev })}
        </Pill>

        {/* **Le mot tombe sous 52 rem** *(2026-08-15)* — mais pas l'état : le
            libellé portait aussi « Enregistrement… », donc le retirer effacerait
            la seule chose qui dise que le geste est parti. La coche devient une
            roue le temps de l'aller-retour, et le nom accessible suit.

            **Et le dessin change avec l'état** *(2026-08-15)*. La coche restait
            la même que la note ait été touchée ou non : or une coche dit « c'est
            enregistré », c'est-à-dire l'inverse de ce qu'il y a à faire quand la
            pastille annonce « Modifié ». Trois états, trois dessins — rien à
            faire (coche), quelque chose à envoyer (nuage montant), en vol
            (roue) —, et le mot suit le dessin. */}
        <Action
          testId="editor-save"
          variant="primary"
          label={saveLabel(state.saving, editorDirty.value)}
          onClick={() => void onSaveNote()}
          alwaysEnabled
        >
          {state.saving ? (
            <span class="spinner" aria-hidden="true" />
          ) : editorDirty.value ? (
            <IconCloudUp size={18} />
          ) : (
            <IconCheck size={18} />
          )}
          <span class="label-wide">{saveLabel(state.saving, editorDirty.value)}</span>
        </Action>

        {/* **Ce qu'on ne fait pas à chaque note**, derrière un point d'entrée
            unique : la vue du Markdown et l'historique des révisions. Ce sont deux
            fonctions importantes — le retour d'usage le dit — mais pas deux
            fonctions permanentes, et elles prenaient à « Enregistrer » la place
            qui lui manquait. */}
        <Popover
          testId="editor-more"
          label={t('editor.more')}
          width={252}
          align="end"
          buttonClass="icon-btn"
          trigger={() => <IconMore size={18} />}
        >
          {(closeMenu) => (
            <>
              <button
                type="button"
                role="menuitemradio"
                aria-checked={state.mode === 'source'}
                class={state.mode === 'source' ? 'current' : undefined}
                data-testid="editor-mode"
                onClick={() => {
                  closeMenu();
                  // Le passage **source → clair** doit repeindre le document : le
                  // texte a pu y être réécrit à la main, et le compteur est le
                  // seul signal que la zone d'édition écoute.
                  patchEditor(
                    state.mode === 'rich'
                      ? { mode: 'source' }
                      : { mode: 'rich', loaded: state.loaded + 1 },
                  );
                }}
              >
                {state.mode === 'rich' ? <IconCode size={18} /> : <IconEdit size={18} />}
                {/* **Un mode se nomme, une action se conjugue.** L'entrée dit le
                    mode où elle mène ; « Écrire » y désignait un mode et, deux
                    écrans plus tôt, l'action d'ouvrir l'éditeur. */}
                <span>{state.mode === 'rich' ? t('editor.modeSource') : t('editor.modeRich')}</span>
              </button>

              {/* **Absente** et non grisée sur une note reçue : la route rendrait
                  403, et un contrôle désactivé annonce une fonction qui n'existe
                  pas pour cet utilisateur. */}
              {owner ? (
                <button
                  type="button"
                  role="menuitem"
                  data-testid="editor-history"
                  onClick={() => {
                    closeMenu();
                    void onLoadHistory();
                  }}
                >
                  <IconHistory size={18} />
                  <span>{t('editor.history')}</span>
                </button>
              ) : null}
            </>
          )}
        </Popover>
      </div>

      {/* **La porte de sortie** *(2026-08-16)*, offerte seulement après qu'une
          sortie a échoué. Elle est au-dessus du conflit parce qu'elle en est la
          suite : le bandeau de conflit propose quoi faire *du texte*, celui-ci
          propose quoi faire *de l'écran* quand aucune des issues n'a marché. */}
      {state.leaving ? (
        <div class="notice error" data-testid="editor-leave" role="alert">
          <span>{t('editor.leaveUnsaved')}</span>
          <Action
            testId="editor-leave-discard"
            variant="danger"
            onClick={close}
            alwaysEnabled
          >
            {t('editor.leaveDiscard')}
          </Action>
          <Action testId="editor-stay" variant="quiet" onClick={onStayInEditor} alwaysEnabled>
            {t('editor.stay')}
          </Action>
        </div>
      ) : null}

      {state.conflict ? <Conflict conflict={state.conflict} /> : null}

      {/* **La barre de mise en forme n'existe qu'en clair.** En mode source, on
          écrit la syntaxe : des boutons qui insèrent du HTML y produiraient du
          texte que personne n'a demandé. */}
      {state.mode === 'rich' ? <FormatBar /> : null}

      <div class="editor-stage">
        {state.mode === 'rich' ? (
          <RichNote
            markdown={state.text}
            docKey={state.loaded}
            ariaLabel={t('editor.body')}
            onInput={(text) => patchEditor({ text })}
          />
        ) : (
          <textarea
            ref={area}
            class="editor-area"
            data-testid="editor-text"
            aria-label={t('editor.body')}
            spellcheck
            value={state.text}
            onInput={(event) => patchEditor({ text: event.currentTarget.value })}
          />
        )}

        {state.history ? <History /> : null}
      </div>
    </div>
  );
}

/**
 * **Le titre s'écrit là où s'écrit la note** *(2026-08-14)*.
 *
 * Il était un texte : renommer demandait de fermer l'éditeur, de retrouver
 * l'objet, d'ouvrir sa fiche, d'y taper le nom. Quatre écrans pour un mot, alors
 * qu'une note se nomme **pendant** qu'on l'écrit — souvent parce que ce qu'on
 * vient d'écrire n'est plus ce que le nom annonçait.
 *
 * Deux précautions, et chacune évite une écriture inutile :
 *
 * - **On enregistre à la sortie du champ ou sur `Entrée`**, jamais à la frappe :
 *   `PATCH /files/{fid}` par caractère produirait une écriture par lettre, et
 *   `UpdatedAt` avancerait autant de fois.
 * - **Un nom inchangé, ou vide, n'écrit rien.** Le champ retombe alors sur le nom
 *   courant : un titre vide n'est pas une intention, c'est une frappe en cours.
 *
 * Le nom est aussi recopié dans l'état de l'éditeur, parce que c'est lui qui
 * nourrit le libellé accessible de la boîte de dialogue — sans quoi le titre
 * changerait à l'écran et pas pour qui l'écoute.
 */
/**
 * Le mot du bouton, **accordé au dessin** : « Enregistrement… » pendant le vol,
 * « Enregistrer » quand il y a quelque chose à envoyer, « Enregistré » quand il
 * n'y a rien à faire — un impératif sur un bouton inutile ferait chercher ce
 * qu'on aurait oublié.
 */
function saveLabel(saving: boolean, dirty: boolean): string {
  if (saving) return t('editor.saving');
  return dirty ? t('editor.save') : t('editor.saved');
}

function TitleField(): JSX.Element | null {
  const state = editor.value;
  const [value, setValue] = useState(state?.entity.Name ?? '');
  // Le nom vient d'ailleurs quand une révision est restaurée : le champ doit
  // suivre l'entité, et `docKey` joue ici le rôle que `loaded` joue pour le corps.
  useEffect(() => setValue(state?.entity.Name ?? ''), [state?.entity.Fid, state?.entity.Name]);
  if (!state) return null;

  const commit = (): void => {
    const trimmed = value.trim();
    if (!trimmed || trimmed === state.entity.Name) return setValue(state.entity.Name);
    patchEditor({ entity: { ...state.entity, Name: trimmed } });
    void onRename(state.entity, trimmed);
  };

  return (
    <input
      class="input editor-title"
      data-testid="editor-title"
      aria-label={t('inspector.name')}
      value={value}
      onInput={(ev) => setValue((ev.currentTarget as HTMLInputElement).value)}
      onBlur={commit}
      onKeyDown={(ev) => {
        const key = (ev as KeyboardEvent).key;
        if (key === 'Enter') {
          ev.preventDefault();
          commit();
          (ev.currentTarget as HTMLInputElement).blur();
        }
        // `Échap` rend le nom d'origine plutôt que de fermer l'éditeur : on est
        // dans un champ, et la touche y annule la saisie — fermer la note entière
        // parce qu'on renonce à la renommer serait une surprise coûteuse.
        if (key === 'Escape') {
          ev.stopPropagation();
          setValue(state.entity.Name);
          (ev.currentTarget as HTMLInputElement).blur();
        }
      }}
    />
  );
}

/**
 * Le bandeau de conflit. **Quatre situations, quatre issues** — les confondre
 * sous un « échec de l'enregistrement » laisserait l'utilisateur sans conduite à
 * tenir, alors que chacune en a une, et une seule.
 */
function Conflict({ conflict }: { readonly conflict: ConflictState }): JSX.Element {
  const reload = (
    <Action testId="editor-reload" variant="primary" onClick={() => void onReloadNote()} alwaysEnabled>
      {t('editor.reload')}
    </Action>
  );

  switch (conflict.kind) {
    case 'stale':
      // Quelqu'un a enregistré depuis. Le texte local **n'est pas jeté** : il
      // reste dans la zone de frappe, et c'est l'utilisateur qui arbitre.
      return (
        <div class="notice error" data-testid="editor-conflict" data-reason="stale" role="alert">
          <span>{t('editor.conflictStale', { rev: conflict.currentRev })}</span>
          {reload}
          <Action
            testId="editor-force"
            onClick={() => {
              // Réenregistrer **sur la base fraîche** : ce n'est pas un vol de
              // verrou, c'est reprendre le protocole avec la bonne base, texte
              // local conservé. L'écrasement reste un choix explicite.
              patchEditor({ baseRev: conflict.currentRev, conflict: undefined });
              void onSaveNote();
            }}
            alwaysEnabled
          >
            {t('editor.keepMine')}
          </Action>
        </div>
      );

    case 'locked':
      return (
        <div class="notice error" data-testid="editor-conflict" data-reason="locked" role="alert">
          <span>
            {conflict.expiresAt
              ? t('editor.conflictLockedUntil', { at: formatDateTime(conflict.expiresAt) })
              : t('editor.conflictLocked')}
          </span>
          <Action testId="editor-retry" onClick={() => void onSaveNote()} alwaysEnabled>
            {t('editor.retry')}
          </Action>
        </div>
      );

    case 'lockLost':
      // **Ne pas rejouer le `complete`** : il faut repartir du début du
      // protocole, et relire est la seule façon d'avoir une base juste.
      return (
        <div class="notice error" data-testid="editor-conflict" data-reason="lockLost" role="alert">
          <span>{t('editor.conflictLockLost', { rev: conflict.currentRev })}</span>
          {reload}
        </div>
      );

    case 'missingBytes':
      return (
        <div class="notice error" data-testid="editor-conflict" data-reason="missingBytes" role="alert">
          <span>{t('editor.conflictMissingBytes')}</span>
          <Action testId="editor-retry" onClick={() => void onSaveNote()} alwaysEnabled>
            {t('editor.retry')}
          </Action>
        </div>
      );
  }
}

/**
 * L'historique. Chaque ligne porte **l'instantané des métadonnées** de l'époque —
 * le serveur les range dans l'item de révision, et c'est ce qui rend vraie la
 * promesse « restaurer restitue le corps *et* le titre ».
 *
 * « Restaurer » ne réécrit rien tout de suite : il **ramène le texte dans la zone
 * de frappe**. L'utilisateur voit ce qu'il restaure avant que ce soit enregistré,
 * et garde la main jusqu'au bout.
 */
function History(): JSX.Element {
  const state = editor.value;
  const revisions = state?.history ?? [];

  return (
    <aside class="editor-history" data-testid="editor-history-panel" aria-label={t('editor.history')}>
      <div class="editor-history-head">
        <strong>{t('editor.history')}</strong>
        <IconButton
          testId="editor-history-close"
          label={t('viewer.close')}
          onClick={() => patchEditor({ history: undefined })}
        >
          <IconClose size={16} />
        </IconButton>
      </div>

      {revisions.length === 0 ? (
        <p class="viewer-empty" data-testid="editor-history-empty">
          {t('editor.historyEmpty')}
        </p>
      ) : (
        <ol class="editor-history-list">
          {revisions.map((rev) => (
            <li key={rev.rev} data-testid="editor-revision">
              <div class="editor-revision-head">
                <strong>{t('editor.revision', { rev: rev.rev })}</strong>
                <span>{formatSize(rev.size)}</span>
              </div>
              <span class="editor-revision-at">{formatDateTime(rev.at)}</span>
              {/* Le nom de l'époque : il a pu changer depuis, et c'est justement
                  ce qu'une restauration ramènerait. */}
              <span class="editor-revision-name">{rev.name}</span>
              <Action testId="editor-restore" onClick={() => void onRestore(rev.rev)}>
                {t('editor.restore')}
              </Action>
            </li>
          ))}
        </ol>
      )}
    </aside>
  );
}
